import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ConversationProvider, useConversation } from '../contexts/ConversationContext';
import { PersonaChatAdapter } from '../adapters/personaChatAdapter';
import { ConversationInterface } from '../components/conversation/ConversationInterface';
import { usePersonaToolProgress } from '../hooks/usePersonaToolProgress';
import * as personaService from '../services/personaService';
import * as chatService from '../services/chatService';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { FiArrowLeft, FiMessageSquare, FiSettings } from 'react-icons/fi';
import { LlmDataWarning } from '../components/common/LlmDataWarning';
import PersonaBadge from '../components/common/PersonaBadge';
import { PersonaHoverCard } from '../components/personas/PersonaHoverCard';
import { SkillBadge } from '../components/skills/SkillBadge';
import { SkillPicker } from '../components/skills/SkillPicker';
import { useSkillsForSession, useAttachToSession } from '../hooks/useSkills';
import ChipManager, { ChatChip } from '../components/work/ChipManager';
import { loadPersonaChips, savePersonaChips } from '../components/personas/PersonaChipDefaults';
import ChatDagCanvas from '../components/conversation/chatdag/ChatDagCanvas';
import { ChatDagErrorBoundary } from '../components/conversation/chatdag/ChatDagErrorBoundary';
import {
  PersonaChatUIProvider,
  usePanelHandlerRefs,
} from '../contexts/PersonaChatUIContext';
import { ChatDagRuntimeProvider, EMPTY_COLLAPSED, EMPTY_PHASE_EVENTS } from '../contexts/ChatDagRuntimeContext';
import { ChatDagDimensionsProvider } from '../contexts/ChatDagDimensionsContext';
import type { PhaseEvent } from '../hooks/usePersonaToolProgress';

// v0.3 Chat DAG fan expansion — see PersonaChatInner below for the
// reducer wiring. Lives at module scope so React doesn't recreate it
// every render.
type FanAction = { type: 'toggle'; id: string };
function fanReducer(state: ReadonlySet<string>, action: FanAction): ReadonlySet<string> {
  if (action.type !== 'toggle') return state;
  const next = new Set(state);
  if (next.has(action.id)) next.delete(action.id);
  else next.add(action.id);
  return next;
}

// v0.3 Task 10: IntersectionObserver on chat bubbles — whatever
// bubble is centered in the viewport owns the current `viewportTurnId`
// (by turn-key). Dimmed opacity in DAG renderers uses this to darken
// non-viewport turns. Lives inside ConversationProvider so it can
// walk `state.messages` to map msgId → turnKey on each update.
// v0.3 Task 11 (architect-triad keystone): lift usePersonaToolProgress
// out of PersonaConversationSurface so there's ONE WS subscription
// per chat session. Pipe gates the hook via state.status === 'loading'
// (idle reads shouldn't hold a subscription) and writes outputs back
// to PersonaChatInner state so both the DAG runtime context and the
// conversation surface render from a single source.
const LiveProgressPipe: React.FC<{
  sessionId: string;
  onUpdate: (
    loadingStatus: string,
    phaseEvents: PhaseEvent[],
    wsLastEventMs: number | null,
    inFlightTurnId: string | null,
    errorTurnId: string | null,
  ) => void;
}> = ({ sessionId, onUpdate }) => {
  const { state } = useConversation();
  const enabled = state.status === 'loading';
  const { loadingStatus, phaseEvents } = usePersonaToolProgress({
    sessionId,
    enabled,
  });
  // wsLastEventMs: bump when phaseEvents array grows. Used by the DAG
  // WS-health watchdog (Task 13) to detect a silent socket.
  const lastCountRef = React.useRef(0);
  const lastEventMsRef = React.useRef<number | null>(null);
  // Find the most recent user message without a following assistant
  // message (the "open" turn). Used for both inFlightTurnId (loading)
  // and errorTurnId (error).
  const openTurnUserId = React.useMemo(() => {
    const msgs = state.messages;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'user') {
        const hasFollowingAsst = msgs
          .slice(i + 1)
          .some((m) => m.role === 'assistant');
        return hasFollowingAsst ? null : msgs[i].id;
      }
    }
    return null;
  }, [state.messages]);
  const inFlightTurnId = enabled ? openTurnUserId : null;
  const errorTurnId = state.status === 'error' ? openTurnUserId : null;
  React.useEffect(() => {
    if (!enabled) {
      lastCountRef.current = 0;
      lastEventMsRef.current = null;
      onUpdate('', EMPTY_PHASE_EVENTS, null, null, errorTurnId);
      return;
    }
    if (phaseEvents.length !== lastCountRef.current) {
      lastCountRef.current = phaseEvents.length;
      lastEventMsRef.current = Date.now();
    }
    onUpdate(
      loadingStatus,
      phaseEvents,
      lastEventMsRef.current,
      inFlightTurnId,
      errorTurnId,
    );
  }, [enabled, loadingStatus, phaseEvents, onUpdate, inFlightTurnId, errorTurnId]);
  return null;
};

const ScrollObserver: React.FC<{
  onViewportTurn: (id: string | null) => void;
}> = ({ onViewportTurn }) => {
  const { state } = useConversation();
  const messages = state.messages;
  React.useEffect(() => {
    // Build a msgId → turnKey map once per messages change. Turn key
    // is the preceding user message id (and is itself for user msgs).
    const byId = new Map<string, string>();
    let lastUser: string | null = null;
    for (const m of messages) {
      if (m.role === 'user') {
        lastUser = m.id;
        byId.set(m.id, m.id);
      } else if (m.role === 'assistant' && lastUser) {
        byId.set(m.id, lastUser);
      }
    }

    let raf: number | null = null;
    let timer: number | null = null;
    let pending: string | null = null;

    const io = new IntersectionObserver(
      (entries) => {
        const hit = entries.find((e) => e.isIntersecting);
        if (!hit) return;
        const msgId = hit.target.getAttribute('data-message-id');
        if (!msgId) return;
        const turnKey = byId.get(msgId) ?? msgId;
        if (turnKey === pending) return;
        pending = turnKey;
        if (raf != null) cancelAnimationFrame(raf);
        if (timer != null) window.clearTimeout(timer);
        raf = requestAnimationFrame(() => {
          timer = window.setTimeout(() => {
            onViewportTurn(turnKey);
          }, 300); // v0.4 Task 11: was 80ms; bumped so structural
          //          relayout under focal change doesn't flutter as
          //          the user scrolls.
        });
      },
      { rootMargin: '-30% 0% -30% 0%', threshold: 0 },
    );

    document.querySelectorAll('[data-message-id]').forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      if (raf != null) cancelAnimationFrame(raf);
      if (timer != null) window.clearTimeout(timer);
    };
  }, [messages, onViewportTurn]);
  return null;
};

// Task 30-retry: small session-cost chip for the rich persona card.
// Must render INSIDE ConversationProvider so it can read messages;
// the persona card was hoisted inside the provider for this chip.
const SessionCostChip: React.FC = () => {
  const { darkMode } = useTheme();
  const { state } = useConversation();
  let total = 0;
  state.messages.forEach((msg) => {
    if (msg.role !== 'assistant') return;
    const tt = (msg.metadata as any)?.turn_telemetry;
    if (tt?.estimated_cost_usd != null) total += Number(tt.estimated_cost_usd) || 0;
  });
  if (total === 0) return null;
  const fmt =
    total < 0.001
      ? `$${total.toFixed(5)}`
      : total < 0.01
        ? `$${total.toFixed(4)}`
        : total < 1
          ? `$${total.toFixed(3)}`
          : `$${total.toFixed(2)}`;
  return (
    <span
      className={`flex items-center gap-1 text-[11px] font-mono ${
        darkMode ? 'text-emerald-400' : 'text-emerald-700'
      }`}
      title={`Session total estimated cost: ${fmt}`}
    >
      💰 {fmt}
    </span>
  );
};

const PersonaChipBar: React.FC = () => {
  const { darkMode } = useTheme();
  const { sendMessage, state } = useConversation();
  const [showChipManager, setShowChipManager] = useState(false);
  const [chips, setChips] = useState<ChatChip[]>(loadPersonaChips);
  const visibleChips = chips.filter((c) => !c.hidden);

  const handleChipsChange = useCallback((newChips: ChatChip[]) => {
    setChips(newChips);
    savePersonaChips(newChips);
  }, []);

  return (
    <>
      <div
        className={`flex flex-wrap items-center gap-x-1 gap-y-1 px-2.5 py-1 border-b shrink-0 ${
          darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
        }`}
      >
        {visibleChips.map((chip) => {
          const previewMax = 200;
          const promptPreview =
            chip.prompt.length > previewMax
              ? `${chip.prompt.slice(0, previewMax)}…`
              : chip.prompt;
          return (
            <button
              key={chip.id}
              disabled={state.status === 'loading'}
              title={promptPreview}
              className={`text-[10px] leading-tight px-2 py-0.5 rounded-full whitespace-nowrap transition-colors ${
                state.status === 'loading' ? 'opacity-50 cursor-not-allowed' : ''
              } ${
                darkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                  : 'bg-white border border-gray-200 hover:bg-gray-100 text-gray-600'
              }`}
              onClick={() =>
                state.status !== 'loading' &&
                sendMessage(chip.prompt, {
                  // Marks the user-bubble for compact chip-fire rendering
                  // (MessageBubble reads `metadata.template.label`). Keeps
                  // the full prompt as `content` so search / re-send /
                  // export still see the real text.
                  template: {
                    id: chip.id,
                    label: chip.label,
                    source: 'persona_chip',
                  },
                })
              }
            >
              {chip.label}
            </button>
          );
        })}
        <button
          onClick={() => setShowChipManager(true)}
          className={`p-0.5 rounded-full shrink-0 transition-colors ${
            darkMode
              ? 'text-gray-500 hover:text-gray-300 hover:bg-gray-700'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
          title="Manage chips"
        >
          <FiSettings className="w-2.5 h-2.5" />
        </button>
      </div>
      <ChipManager
        open={showChipManager}
        onClose={() => setShowChipManager(false)}
        chips={chips}
        onChipsChange={handleChipsChange}
      />
    </>
  );
};

// Surface that lives INSIDE ConversationProvider so it can read state.status
// and drive the live persona-chat tool-progress hook. The backend broadcasts
// chat_tool_invoked / _completed / _timeout / _error / _budget_exceeded over
// WS; the hook maps them to a loadingStatus string, and MessageList appends
// each distinct phase to its statusHistory so the user sees the tool trail.
const PersonaConversationSurface: React.FC<{
  persona: any;
  sessionId: string;
  isFallback: boolean;
  loadingStatus: string;
  phaseEvents: PhaseEvent[];
}> = ({ persona, sessionId, isFallback, loadingStatus, phaseEvents }) => {
  return (
    <ConversationInterface
      title={persona.name}
      subtitle={
        isFallback
          ? persona.shortDescription || 'AI Assistant'
          : persona.primarySpecialization || 'AI Assistant'
      }
      avatarName={persona.name}
      avatarSpecialization={isFallback ? undefined : persona.primarySpecialization}
      avatarUrl={isFallback ? undefined : persona.avatarUrl}
      sessionId={sessionId}
      personaId={persona.id}
      personaName={persona.name}
      compactHeader={!isFallback}
      loadingStatus={loadingStatus}
      phaseEvents={phaseEvents}
    />
  );
};

interface PersonaChatInnerProps {
  persona: any;
  session: any | undefined;
  sessionId: string;
  adapter: PersonaChatAdapter;
  isFallback?: boolean;
  sessionSkills?: any[];
  onShowSkillPicker?: () => void;
  onBack: () => void;
}

const PersonaChatInner: React.FC<PersonaChatInnerProps> = ({
  persona,
  session,
  sessionId,
  adapter,
  isFallback = false,
  sessionSkills = [],
  onShowSkillPicker,
  onBack,
}) => {
  const { darkMode } = useTheme();
  const [showPersonaDetails, setShowPersonaDetails] = useState(false);
  const personaDetailsRef = useRef<HTMLDivElement>(null);
  // Task 29 — Panel handler refs wire in-bubble click-throughs
  // (CreateArtifactsResult) to the Panel's imperative focus/scroll
  // without prop-drilling through MessageList → MessageBubble →
  // ToolCallPill → CreateArtifactsResult.
  const panelHandlerRefs = usePanelHandlerRefs();

  // v0.3 Chat DAG: Set of parent tool_call_ids whose peer fan is
  // currently user-expanded. The FanCollapsedChip toggles membership
  // via toggleFanExpansion on PersonaChatUIContext; the processor
  // reads via ChatDagRuntimeContext.collapsedFanIds. Reducer lives at
  // PersonaChatInner scope so the set survives re-renders without
  // leaking into component-level state.
  const [collapsedFanIds, dispatchFan] = React.useReducer(fanReducer, EMPTY_COLLAPSED);
  const toggleFanExpansion = React.useCallback(
    (id: string) => dispatchFan({ type: 'toggle', id }),
    [],
  );

  // v0.3 Task 10: viewportTurnId drives opacity dimming in DAG nodes.
  // Fed by <ScrollObserver> mounted inside ConversationProvider.
  const [viewportTurnId, setViewportTurnId] = React.useState<string | null>(null);

  // v0.3 controls-panel globals — surfaced via the DAG's ControlsPanel.
  const [allFansCollapsed, setAllFansCollapsed] = React.useState<boolean>(false);
  const [showPhaseStacks, setShowPhaseStacks] = React.useState<boolean>(true);
  // Per-turn phase-stack collapse state — fanReducer is reused since
  // the semantics are identical (toggle-membership-of-a-string-set).
  const [collapsedPhaseStackIds, dispatchPhaseStack] = React.useReducer(
    fanReducer,
    EMPTY_COLLAPSED,
  );
  const togglePhaseStackCollapse = React.useCallback(
    (turnKey: string) => dispatchPhaseStack({ type: 'toggle', id: turnKey }),
    [],
  );
  const [allPhaseStacksCollapsed, setAllPhaseStacksCollapsed] =
    React.useState<boolean>(false);

  // v0.3 Task 11: single source of live tool progress for this chat.
  // Fed by <LiveProgressPipe> inside ConversationProvider; read by
  // PersonaConversationSurface for the legacy loadingStatus display
  // and by ChatDagRuntimeProvider for live phase events.
  const [liveLoadingStatus, setLiveLoadingStatus] = React.useState<string>('');
  const [livePhaseEvents, setLivePhaseEvents] = React.useState<PhaseEvent[]>(EMPTY_PHASE_EVENTS);
  const [wsLastEventMs, setWsLastEventMs] = React.useState<number | null>(null);
  const [inFlightTurnId, setInFlightTurnId] = React.useState<string | null>(null);
  const [errorTurnId, setErrorTurnId] = React.useState<string | null>(null);

  // v0.4 Task 1: focal-state ownership. tabFocusTurnId set by
  // keyboard-focus on a DAG node (Task 11 wires useTabFocalTrigger).
  // lastInFlightTurnId captures the most recent turn that was in-flight
  // so the focal chain keeps that turn "hot" for a moment after the
  // assistant response lands (cleared on scroll in Task 11).
  // inFlightStartedAtMs gates the first-2s RICH_DEFAULT authoritative
  // window in layoutChatDag (Task 8 resolveDims).
  const [tabFocusTurnId, setTabFocusTurnId] = React.useState<string | null>(
    null,
  );
  const [lastInFlightTurnId, setLastInFlightTurnId] = React.useState<
    string | null
  >(null);
  const [inFlightStartedAtMs, setInFlightStartedAtMs] = React.useState<
    number | null
  >(null);
  const prevInFlightRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const prev = prevInFlightRef.current;
    if (prev != null && inFlightTurnId == null) {
      // Turn ended — keep it as "lastInFlight" so the focal chain
      // doesn't immediately snap away before the answer is read.
      setLastInFlightTurnId(prev);
      setInFlightStartedAtMs(null);
    } else if (prev == null && inFlightTurnId != null) {
      // Turn started — stamp the start time for the first-2s window.
      setInFlightStartedAtMs(Date.now());
    }
    prevInFlightRef.current = inFlightTurnId;
  }, [inFlightTurnId]);

  // v0.4 Task 11: scroll clears lastInFlightTurnId so the focal
  // chain doesn't hold the just-completed turn "hot" when the user
  // scrolls away. Capture=true so any scrollable ancestor fires.
  React.useEffect(() => {
    const handler = () => setLastInFlightTurnId(null);
    window.addEventListener('scroll', handler, {
      passive: true,
      capture: true,
    });
    return () => window.removeEventListener('scroll', handler, true);
  }, []);

  const handleLiveProgressUpdate = React.useCallback(
    (
      status: string,
      events: PhaseEvent[],
      ms: number | null,
      turnId: string | null,
      errId: string | null,
    ) => {
      setLiveLoadingStatus(status);
      setLivePhaseEvents(events);
      setWsLastEventMs(ms);
      setInFlightTurnId(turnId);
      setErrorTurnId(errId);
    },
    [],
  );

  // Task 13: WS-health watchdog. When loading + no phase events for
  // 120s, toast once per open turn. Resets when status leaves
  // 'loading'. Distinct from the backend's turn-lifecycle watchdog
  // (which owns its own time budget); this watchdog tells the USER
  // that the DAG may be stale.
  const { showToast } = useToast();
  const [healthToastShown, setHealthToastShown] = React.useState(false);
  React.useEffect(() => {
    if (inFlightTurnId == null) {
      setHealthToastShown(false);
    }
  }, [inFlightTurnId]);
  React.useEffect(() => {
    if (inFlightTurnId == null) return;
    const t = window.setInterval(() => {
      if (wsLastEventMs == null) return;
      if (Date.now() - wsLastEventMs > 120_000 && !healthToastShown) {
        showToast(
          'Connection may be degraded — DAG state may be stale.',
          'error',
        );
        setHealthToastShown(true);
      }
    }, 5000);
    return () => window.clearInterval(t);
  }, [inFlightTurnId, wsLastEventMs, healthToastShown, showToast]);

  // v0.3 spine click: locate the bubble by data-message-id and
  // scroll its WRAPPER (which includes any PersistedPhaseTrail
  // sitting above the bubble) to the top of the scroll viewport.
  // This matches the turn-end scroll methodology in MessageList —
  // phase trail collapsed at top, then the message content below —
  // so a DAG-driven jump and a turn-end-driven scroll leave the user
  // in the same visual frame. Returns false if the bubble isn't in
  // the DOM (virtualization / not-loaded history).
  const focusBubble = React.useCallback(
    (messageId: string): boolean => {
      const el = document.querySelector(`[data-message-id="${messageId}"]`);
      if (!el) {
        showToast(
          'Message not in view — scroll the history or wait for load.',
          'info',
        );
        return false;
      }
      // MessageBubble's React fragment puts PersistedPhaseTrail as
      // the bubble's sibling inside the wrapper div created by
      // MessageList — so bubble.parentElement is the wrapper and
      // scrolling ITS top pins the phase trail + bubble top together.
      const target = (el.parentElement as HTMLElement | null) ?? el;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('chatdag-bubble-flash');
      window.setTimeout(() => {
        el.classList.remove('chatdag-bubble-flash');
      }, 1400);
      return true;
    },
    [showToast],
  );

  // Tools Panel collapse state. Defaults to closed because most
  // users don't need the full tool-call feed — it's an operator
  // tool. Preference is persisted so returning sessions remember.
  // Auto-opens when an artifact click fires from a bubble (via
  // PersonaChatUIProvider's onRequestPanelOpen).
  const [panelOpen, setPanelOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('personaChatPanelOpen') === '1';
    } catch {
      return false;
    }
  });
  // Panel width (px) is user-draggable via the rail. Clamped to
  // [180, 800] on drag; below 60 during drag auto-collapses.
  const PANEL_WIDTH_MIN = 180;
  const PANEL_WIDTH_MAX = 800;
  const PANEL_WIDTH_AUTO_CLOSE = 60;
  const PANEL_WIDTH_DEFAULT = 420;
  const [panelWidthPx, setPanelWidthPx] = useState<number>(() => {
    try {
      const raw = localStorage.getItem('personaChatPanelWidth');
      const n = raw ? parseInt(raw, 10) : NaN;
      if (!Number.isNaN(n) && n >= PANEL_WIDTH_MIN && n <= PANEL_WIDTH_MAX) return n;
    } catch {
      // fall through to default
    }
    return PANEL_WIDTH_DEFAULT;
  });
  useEffect(() => {
    try {
      localStorage.setItem('personaChatPanelOpen', panelOpen ? '1' : '0');
    } catch {
      // Local storage unavailable — tolerate + move on.
    }
  }, [panelOpen]);
  useEffect(() => {
    try {
      localStorage.setItem('personaChatPanelWidth', String(panelWidthPx));
    } catch {
      // Same tolerance as above.
    }
  }, [panelWidthPx]);

  // Drag state for the rail. Ref holds starting positions + a
  // `moved` flag so pointer-up can distinguish a click (toggle) from
  // a drag (commit width). React state only for the `dragging`
  // boolean so the panel can disable its width-transition while the
  // user is actively resizing (otherwise it visibly lags).
  const dragRef = useRef<{ startX: number; startWidth: number; moved: boolean } | null>(null);
  const [dragging, setDragging] = useState(false);

  const handleRailPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startWidth: panelOpen ? panelWidthPx : 0,
        moved: false,
      };
    },
    [panelOpen, panelWidthPx],
  );

  const handleRailPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const st = dragRef.current;
      if (!st) return;
      // Rail is on the right of the chat column; panel sits to the
      // right of the rail. Dragging left (clientX decreases) should
      // GROW the panel.
      const dx = st.startX - e.clientX;
      if (!st.moved && Math.abs(dx) > 3) {
        st.moved = true;
        setDragging(true);
      }
      if (!st.moved) return;
      const newWidth = st.startWidth + dx;
      if (newWidth < PANEL_WIDTH_AUTO_CLOSE) {
        if (panelOpen) setPanelOpen(false);
      } else {
        if (!panelOpen) setPanelOpen(true);
        const clamped = Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX, newWidth));
        setPanelWidthPx(clamped);
      }
    },
    [panelOpen],
  );

  const handleRailPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Not captured — fine.
      }
      const st = dragRef.current;
      dragRef.current = null;
      setDragging(false);
      // No meaningful movement = treat as click toggle.
      if (st && !st.moved) {
        setPanelOpen((v) => !v);
      }
    },
    [],
  );

  useEffect(() => {
    if (isFallback) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (personaDetailsRef.current && !personaDetailsRef.current.contains(event.target as Node)) {
        setShowPersonaDetails(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFallback]);

  const participants = [
    { id: 'user', name: 'You', type: 'user' as const },
    { id: persona.id, name: persona.name, type: 'persona' as const, metadata: persona },
  ];

  return (
    <div className={`w-full ${darkMode ? 'text-white' : 'text-gray-800'}`}>
      <LlmDataWarning />

      {/* ConversationProvider hoisted to wrap the rich card so its
          💰 session-cost chip can read messages via
          useConversation(). The nested Provider at the layout level
          has been removed; this single outer Provider serves the
          entire page. */}
      <ConversationProvider
        sessionId={sessionId}
        adapter={adapter}
        participants={participants}
        metadata={{ isNew: !isFallback && !sessionId, personaId: persona.id }}
      >
      <ScrollObserver onViewportTurn={setViewportTurnId} />
      <LiveProgressPipe sessionId={sessionId} onUpdate={handleLiveProgressUpdate} />

      {isFallback ? (
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <div className={`mr-3 p-2 rounded-lg ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
              <FiMessageSquare className={`h-6 w-6 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`} />
            </div>
            <div>
              <h1
                className={`text-2xl font-bold ${
                  darkMode
                    ? 'text-blue-300 border-l-4 border-blue-700 pl-3'
                    : 'text-blue-700 border-l-4 border-blue-500 pl-3'
                }`}
              >
                Chat with {persona.name}
              </h1>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {persona.shortDescription || 'AI Assistant'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex items-center gap-2 flex-wrap">
              {sessionSkills.map((s: any) => (
                <SkillBadge key={s.skill_name} name={s.skill_name} />
              ))}
              {onShowSkillPicker && (
                <button
                  onClick={onShowSkillPicker}
                  className={`px-3 py-1.5 rounded-md text-xs flex items-center ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  + Skills
                </button>
              )}
            </div>
            <button
              onClick={onBack}
              className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                darkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white shadow-sm'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700 shadow-sm'
              }`}
            >
              <FiArrowLeft className="mr-1.5" />
              Back to Chats
            </button>
          </div>
        </div>
      ) : (
        <div
          ref={personaDetailsRef}
          className={`rounded-lg shadow-lg border-l-4 px-4 py-3 mb-2 cursor-pointer transition-all hover:shadow-xl relative ${
            darkMode ? 'bg-gray-800 border-emerald-600' : 'bg-white border-emerald-500 shadow-sm'
          }`}
          onClick={() => setShowPersonaDetails(true)}
        >
          <div className="flex gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <PersonaBadge personaId={persona.id} personaName={persona.name} size="lg" clickable={false} hideName />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-base font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    {persona.name.replace(/_/g, ' ')}
                  </span>
                  {persona.revision != null && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      rev {persona.revision}
                    </span>
                  )}
                  {session?.message_count != null && (
                    <span
                      className={`flex items-center gap-1 text-[11px] ${
                        darkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}
                    >
                      <FiMessageSquare className="w-3 h-3" /> {session.message_count} msgs
                    </span>
                  )}
                  {session?.created_at && (
                    <span className={`text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {(() => {
                        try {
                          const d = Date.now() - new Date(session.created_at).getTime();
                          const m = Math.floor(d / 60000);
                          if (m < 60) return `${m}m`;
                          const h = Math.floor(m / 60);
                          if (h < 24) return `${h}h`;
                          return `${Math.floor(h / 24)}d`;
                        } catch {
                          return '';
                        }
                      })()}{' '}
                      session
                    </span>
                  )}
                  <SessionCostChip />
                </div>
                <p className={`text-xs mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {persona.primarySpecialization || persona.shortDescription || 'Conversational AI'}
                </p>
                {persona.tags && persona.tags.length > 0 && (
                  <div className="flex gap-1 flex-wrap mb-1.5">
                    {persona.tags.slice(0, 5).map((tag: string) => (
                      <span
                        key={tag}
                        className={`px-1.5 py-0.5 text-[10px] rounded ${
                          darkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {persona.capabilities && (
                  <div className="flex gap-3">
                    {Object.entries(persona.capabilities as Record<string, any>)
                      .slice(0, 4)
                      .map(([key, val]) => {
                        const score = typeof val === 'object' ? (val as any).score ?? 0 : Number(val) || 0;
                        return (
                          <div key={key} className="flex items-center gap-1">
                            <span
                              className={`text-[9px] uppercase ${
                                darkMode ? 'text-gray-500' : 'text-gray-400'
                              }`}
                            >
                              {key.slice(0, 4)}
                            </span>
                            <div
                              className={`w-12 h-1 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                            >
                              <div
                                className="h-1 rounded-full bg-emerald-500"
                                style={{ width: `${Math.min(100, score * 10)}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-start gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPersonaDetails(!showPersonaDetails);
                }}
                className={`px-2 py-1 rounded text-xs ${
                  darkMode ? 'text-emerald-400 hover:bg-emerald-900/20' : 'text-emerald-600 hover:bg-emerald-50'
                }`}
              >
                Quick View
              </button>
              <button
                onClick={onBack}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                  darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <FiArrowLeft className="w-3 h-3" /> Back
              </button>
            </div>
          </div>
          <PersonaHoverCard persona={persona} isVisible={showPersonaDetails} />
        </div>
      )}

      <div>
        <div
          className={
            isFallback
              ? `rounded-lg shadow-lg p-0 ${
                  darkMode ? 'bg-gray-800 border-l-4 border-blue-700' : 'bg-white border-l-4 border-blue-500'
                }`
              : `rounded-lg shadow-lg overflow-hidden ${
                  darkMode ? 'bg-gray-800 border-l-4 border-emerald-700' : 'bg-white border-l-4 border-emerald-500'
                }`
          }
        >
          <div className={isFallback ? 'h-[calc(100vh-200px)] flex' : 'h-[76vh] flex'}>
            <ChatDagRuntimeProvider
              collapsedFanIds={collapsedFanIds}
              viewportTurnId={viewportTurnId}
              livePhaseEvents={livePhaseEvents}
              wsLastEventMs={wsLastEventMs}
              inFlightTurnId={inFlightTurnId}
              tabFocusTurnId={tabFocusTurnId}
              lastInFlightTurnId={lastInFlightTurnId}
              inFlightStartedAtMs={inFlightStartedAtMs}
              setTabFocusTurnId={setTabFocusTurnId}
              errorTurnId={errorTurnId}
              allFansCollapsed={allFansCollapsed}
              showPhaseStacks={showPhaseStacks}
              collapsedPhaseStackIds={collapsedPhaseStackIds}
              allPhaseStacksCollapsed={allPhaseStacksCollapsed}
            >
            <ChatDagDimensionsProvider>
            <PersonaChatUIProvider
              handlerRefs={panelHandlerRefs}
              onRequestPanelOpen={() => setPanelOpen(true)}
              onToggleFanExpansion={toggleFanExpansion}
              onFocusBubble={focusBubble}
              onSetAllFansCollapsed={setAllFansCollapsed}
              onSetShowPhaseStacks={setShowPhaseStacks}
              onTogglePhaseStackCollapse={togglePhaseStackCollapse}
              onSetAllPhaseStacksCollapsed={setAllPhaseStacksCollapsed}
            >
              {/* Content column expands to fill when the ChatMap panel
                  is collapsed; the panel lives in a transitioning-width
                  column on the right. The rail between them is always
                  visible and is the primary open/close affordance so
                  users never lose sight of how to get the panel back. */}
              <div className="flex-1 flex flex-col border-r border-gray-700/40 min-w-0 transition-[flex-basis] duration-200">
                <PersonaChipBar />
                <div className="flex-1 min-h-0">
                  <PersonaConversationSurface
                    persona={persona}
                    sessionId={sessionId}
                    isFallback={isFallback}
                    loadingStatus={liveLoadingStatus}
                    phaseEvents={livePhaseEvents}
                  />
                </div>
              </div>

              {/* Always-visible draggable rail. Click toggles
                  open/close; drag resizes the panel; drag-past-min
                  auto-collapses. Vertical gradient "🔗 ChatMap" label
                  — colorful + eye-catching so the affordance
                  telegraphs "something lives here" without needing
                  a user to know to hunt for it. `cursor-col-resize`
                  hints at draggability. */}
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label={panelOpen ? 'Resize or hide ChatMap panel' : 'Resize or show ChatMap panel'}
                aria-expanded={panelOpen}
                aria-controls="persona-chatdag-panel"
                onPointerDown={handleRailPointerDown}
                onPointerMove={handleRailPointerMove}
                onPointerUp={handleRailPointerUp}
                onPointerCancel={handleRailPointerUp}
                className={`group flex flex-col items-center justify-center gap-3 w-7 shrink-0 select-none cursor-col-resize border-r touch-none ${
                  darkMode
                    ? 'border-gray-700/40 hover:bg-emerald-900/20 active:bg-emerald-900/30'
                    : 'border-gray-200 hover:bg-emerald-50 active:bg-emerald-100'
                } ${dragging ? (darkMode ? 'bg-emerald-900/30' : 'bg-emerald-100') : ''}`}
              >
                <span
                  className={`text-sm font-bold ${
                    darkMode ? 'text-emerald-300' : 'text-emerald-600'
                  } group-hover:scale-125 transition-transform`}
                >
                  {panelOpen ? '›' : '‹'}
                </span>
                <span className="text-base" aria-hidden>
                  🔗
                </span>
                <span
                  className="text-[11px] font-bold uppercase tracking-widest bg-gradient-to-b from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent drop-shadow"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                >
                  ChatMap
                </span>
                {/* Tiny grip-dots hint that the rail is grabbable. */}
                <span
                  className={`flex flex-col gap-0.5 ${
                    darkMode ? 'text-emerald-400/60' : 'text-emerald-500/70'
                  }`}
                  aria-hidden
                >
                  <span className="block w-0.5 h-0.5 rounded-full bg-current" />
                  <span className="block w-0.5 h-0.5 rounded-full bg-current" />
                  <span className="block w-0.5 h-0.5 rounded-full bg-current" />
                </span>
              </div>

              {/* Panel column — width animates to 0 when collapsed,
                  transition disables during active drag so the width
                  follows the pointer. ChatDagCanvas is the sole
                  resident now (Tools panel removed 2026-04-24); kept
                  always-mounted so the DAG's fit / zoom / camera
                  state survives panel open/close cycles. */}
              <div
                id="persona-chatdag-panel"
                className={`min-w-0 overflow-hidden flex flex-col ${
                  dragging ? '' : 'transition-[width] duration-200 ease-out'
                }`}
                style={{ width: panelOpen ? `${panelWidthPx}px` : '0px' }}
                aria-hidden={!panelOpen}
              >
                <div className="flex-1 min-h-0 relative">
                  <ChatDagErrorBoundary>
                    <ChatDagCanvas primaryPersona={persona} />
                  </ChatDagErrorBoundary>
                </div>
              </div>
            </PersonaChatUIProvider>
            </ChatDagDimensionsProvider>
            </ChatDagRuntimeProvider>
          </div>
        </div>
      </div>
      </ConversationProvider>
    </div>
  );
};

export const PersonaChat: React.FC = () => {
  const { personaId, sessionId } = useParams<{ personaId?: string; sessionId?: string }>();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [adapter, setAdapter] = useState<PersonaChatAdapter | null>(null);
  const [showSkillPicker, setShowSkillPicker] = useState(false);

  // Fetch persona data — always resolve to something usable, even if the persona 404s
  const { data: persona, isLoading: isPersonaLoading, error: personaError } = useQuery(
    ['persona', personaId, sessionId],
    async () => {
      if (sessionId) {
        let sessionData: any = null;
        try {
          sessionData = await chatService.getChatSession(sessionId);
        } catch (error) {
          console.error('Error fetching session data:', error);
          if (personaId && personaId !== 'unknown') {
            return personaService.getPersona(personaId);
          }
          throw error;
        }

        const resolvedPersonaId =
          personaId && personaId !== 'unknown' && personaId !== sessionId
            ? personaId
            : sessionData.persona_id;

        if (resolvedPersonaId && resolvedPersonaId !== 'unknown') {
          try {
            return await personaService.getPersona(resolvedPersonaId);
          } catch (e: any) {
            console.warn(
              `Persona ${resolvedPersonaId} not found (${e?.response?.status ?? e?.message}), falling back to session metadata`,
            );
          }
        }

        return {
          id: resolvedPersonaId || 'unknown',
          name: sessionData.persona_name || 'Unknown Persona',
          shortDescription: 'AI Assistant',
          tags: [],
        };
      }

      return personaId ? personaService.getPersona(personaId) : Promise.reject('No persona ID');
    },
    { enabled: !!personaId || !!sessionId },
  );

  const { data: session, isLoading: isSessionLoading } = useQuery(
    ['chatSession', sessionId],
    async () => chatService.getChatSession(sessionId),
    { enabled: !!sessionId },
  );

  useEffect(() => {
    if (persona) {
      setAdapter(new PersonaChatAdapter(persona));
    }
  }, [persona]);

  const { data: newSessionData, isLoading: isSessionCreating } = useQuery(
    ['createChatSession', personaId],
    async () => {
      if (!sessionId && persona) {
        return chatService.createPersonaChat(persona.id, undefined, { isNew: true });
      }
      return null;
    },
    {
      enabled: !!persona && !sessionId,
      onSuccess: (data) => {
        if (data) {
          const effectivePersonaId = personaId || data.persona_id || 'unknown';
          navigate(`/chat/persona/${effectivePersonaId}/${data.session_id}`, { replace: true });
        }
      },
    },
  );

  const actualSessionIdForSkills = sessionId || newSessionData?.session_id || '';
  const { data: sessionSkills = [] } = useSkillsForSession(actualSessionIdForSkills);
  const attachSkillsMutation = useAttachToSession();

  const isLoading = isPersonaLoading || isSessionLoading || (isSessionCreating && !sessionId) || !adapter;

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-sm text-gray-500">
          {isPersonaLoading
            ? 'Loading persona...'
            : isSessionLoading
            ? 'Loading session...'
            : isSessionCreating
            ? 'Creating session...'
            : 'Loading...'}
        </div>
      </div>
    );
  }

  const isFallback = (personaId === 'unknown' || !personaId) && !!sessionId;

  if (isFallback) {
    const fallbackPersona = persona || {
      id: 'unknown',
      name: 'Chat Assistant',
      shortDescription: 'AI Assistant',
      tags: [],
    };
    const fallbackAdapter = adapter ?? new PersonaChatAdapter(fallbackPersona);
    return (
      <>
        <PersonaChatInner
          persona={fallbackPersona}
          session={session}
          sessionId={sessionId!}
          adapter={fallbackAdapter}
          isFallback
          sessionSkills={sessionSkills}
          onShowSkillPicker={() => setShowSkillPicker(true)}
          onBack={() => navigate('/chat/sessions')}
        />
        <SkillPicker
          isOpen={showSkillPicker}
          onClose={() => setShowSkillPicker(false)}
          onAttach={(names) => {
            if (sessionId) attachSkillsMutation.mutate({ sessionId, skillNames: names });
          }}
          currentSkills={sessionSkills.map((s: any) => s.skill_name)}
        />
      </>
    );
  }

  if (personaError && !persona && !sessionId) {
    console.error('Persona error:', personaError);
    return (
      <div
        className={`${
          darkMode
            ? 'bg-red-900 bg-opacity-20 border-red-800 text-red-300'
            : 'bg-red-50 border-red-200 text-red-700'
        } px-4 py-3 rounded relative border`}
        role="alert"
      >
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> Failed to load persona.</span>
        <button
          className={`mt-3 ${
            darkMode
              ? 'bg-red-800 bg-opacity-50 text-red-200 hover:bg-opacity-70'
              : 'bg-red-100 text-red-800 hover:bg-red-200'
          } px-3 py-1 rounded`}
          onClick={() => navigate('/chat/sessions')}
        >
          Return to Chats
        </button>
      </div>
    );
  }

  if (!persona) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const actualSessionId = sessionId || newSessionData?.session_id || 'temp-session-id';

  return (
    <PersonaChatInner
      persona={persona}
      session={session}
      sessionId={actualSessionId}
      adapter={adapter!}
      onBack={() => navigate('/chat/sessions')}
    />
  );
};
